import { ApolloError, ApolloQueryResult, gql, useQuery } from "@apollo/client";
import { useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";

import {
  Container,
  Step,
  StepButton,
  StepContent,
  Stepper,
  makeStyles,
  StepLabel
} from "@material-ui/core";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import { useTenant } from "src/components/LoggedInAdminProvider";
import PageTitle from "src/components/PageTitle";
import { useSubaccount } from "src/components/SubaccountProvider";
import {
  VAN_INTEGRATION_FIELDS,
  CAMPAIGN_WITH_QUESTIONS,
  JOB_FIELDS,
  CAMPAIGN_WITH_TALKING_POINTS,
  CAMPAIGN_WITH_CALLEE_ADDITIONAL_INFO,
  CAMPAIGN_WITH_TRUST_PRODUCT_FIELDS
} from "src/graphql/fragments";
import {
  getCampaign_campaign,
  getCampaign
} from "src/graphql/generated/getCampaign";
import { JobStatus, CampaignStatus } from "src/graphql/generated/globalTypes";
import { loggedInInfo_loggedInInfo_tenant } from "src/graphql/generated/loggedInInfo";
import { useFeatureFlag } from "src/util/featureFlags";

import Basics from "./Basics";
import CallerInterface from "./CallerInterface";
import Callers from "./Callers";
import CampaignEditorsProvider from "./CampaignEditorsProvider";
import Contacts, { validate as validateContactsSection } from "./Contacts";
import Integration from "./Integration";
import MultiplePasses from "./MultiplePasses";
import Numbers from "./Numbers";
import Questions, { validate as validateQuestionsSection } from "./Questions";
import Results from "./Results";
import RunCampaign from "./RunCampaign";
import StartCampaign from "./StartCampaign";
import Voicemail from "./Voicemail";

const jobKeys = ["contactImportJob", "startJob"] as const;
type JobKey = (typeof jobKeys)[number];

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: "flex",
    flexDirection: "row"
  },
  stepperWrapper: {
    width: "300px"
  },
  pageWrapper: {
    flex: 1,
    padding: `0 ${theme.spacing(6)}`,
    marginBottom: theme.spacing(6)
  },
  title: {
    paddingLeft: theme.spacing(3)
  }
}));

const GET_CAMPAIGN = gql`
  ${VAN_INTEGRATION_FIELDS}
  ${CAMPAIGN_WITH_QUESTIONS}
  ${CAMPAIGN_WITH_TALKING_POINTS}
  ${CAMPAIGN_WITH_CALLEE_ADDITIONAL_INFO}
  ${CAMPAIGN_WITH_TRUST_PRODUCT_FIELDS}
  ${JOB_FIELDS}
  query getCampaign($id: ID!) {
    campaign(id: $id) {
      ...CampaignWithQuestions
      ...CampaignWithTalkingPoints
      ...CampaignWithCalleeAdditionalInfo
      ...CampaignWithTrustProductFields
      id
      created_at
      name
      description
      status
      provider
      token
      answering_machine_drop
      max_call_attempts
      maxRatio
      no_call_window
      answering_machine_drop_call_attempt
      amdSetting
      ivr
      ivr_phone_number
      phone_number_config {
        outbound {
          areaCode
        }
      }
      contactImportJob {
        ...JobFields
      }
      contactsUpload {
        fileName
      }
      voicemailUpload {
        fileName
        publicURL
      }
      startJob {
        ...JobFields
      }
      calleeFields {
        id
        name
        builtInField
        dataSourceFieldName
        sidebarOrder
      }
      vanIntegration {
        vanIntegration {
          ...VanIntegrationFields
        }
        databaseMode
        canvassResultCodes {
          resultCodeId
          name
        }
      }
      callingHours {
        timezone
        startTime
        endTime
      }
      outboundPhoneNumbers {
        id
        phoneNumber
        rotationErrors {
          message
        }
      }
      minimum_outgoing_number_pool_size
      approval_requirement
      voicemail_allowed
      requiredApprovals
      structuredDispositionsCopyMigration
      twilioSubaccount {
        id
        name
        deletedAt
      }
      pusherConfigs {
        appId
        key
        cluster
      }
      customCallerAlert {
        expiredAt
        message
      }
      customEndCard {
        onCompleteMessage
        onPausedMessage
        onOutOfHoursMessage
      }
      onCompleteMessage
      onPausedMessage
      outOfHoursMessage
      useAlternateDropRate
    }
  }
`;

const GET_CAMPAIGN_JOBS = gql`
  query getCampaignJobs($id: ID!) {
    campaign(id: $id) {
      id
      contactImportJob {
        id
        type
        createTime
        startTime
        progress
        status
        result
        errorCode
        errorMessage
        errorDetails
      }
      startJob {
        id
        type
        createTime
        startTime
        progress
        status
        result
        errorCode
        errorMessage
        errorDetails
      }
      status
      ivr_phone_number
      token
      calleeFields {
        id
        name
        builtInField
        dataSourceFieldName
      }
    }
  }
`;

type StepDefinition = {
  name: string;
  slug: string;
  job?: JobKey;
  validator?: (props: {
    tenant: loggedInInfo_loggedInInfo_tenant;
    campaign: getCampaign_campaign;
    subaccountLink: (relativePath: string) => string;
  }) => JSX.Element | null;
} & (
  | {
      // The "Start Campaign" step specifies runValidation: true, which means
      // we show validation errors in the sidebar and also pass those validation
      // errors down to the component
      runValidation: true;
      component: (props: {
        campaign: getCampaign_campaign;
        goToNextSection: () => void;
        validationErrors: JSX.Element[];
        refreshCampaign: () => Promise<ApolloQueryResult<getCampaign>>;
      }) => JSX.Element;
    }
  | {
      // Other steps don't specify runValidation, so we don't pass
      // validationErrors to the component
      runValidation?: false;
      component: (props: {
        campaign: getCampaign_campaign;
        goToNextSection: () => void;
        refreshCampaign: () => Promise<ApolloQueryResult<getCampaign>>;
      }) => JSX.Element;
    }
);

function isVanNotFoundError(error: ApolloError) {
  return error?.graphQLErrors?.every(
    err =>
      err.extensions?.code === "VAN_PROXY_ERROR" &&
      err.extensions?.vanProxyErrorCode === "NotFound"
  );
}

export default function CampaignDetails(): JSX.Element {
  const { subaccountLink } = useSubaccount()!;
  const tenant = useTenant()!;
  const classes = useStyles();
  const history = useHistory();
  const jobWasRunningRef = useRef(false);
  const { id, section } = useParams<{ id: string; section: string }>();
  const { loading, error, data, refetch } = useQuery<getCampaign>(
    GET_CAMPAIGN,
    {
      variables: {
        id: id
      }
    }
  );

  // Poll for job status while background jobs are running
  const { startPolling: startJobPolling, stopPolling: stopJobPolling } =
    useQuery(GET_CAMPAIGN_JOBS, {
      variables: {
        id: id
      }
    });

  const anyJobRunning = jobKeys.some(key => {
    const status = data?.campaign[key]?.status;
    return status === JobStatus.QUEUED || status === JobStatus.RUNNING;
  });

  useEffect(() => {
    if (anyJobRunning) {
      jobWasRunningRef.current = true;
      startJobPolling(3000);
    } else {
      if (jobWasRunningRef.current) {
        refetch().catch(err =>
          console.error("failed to refetch campaign after job completion", err)
        );
      }
      jobWasRunningRef.current = false;
    }

    return () => stopJobPolling();
  }, [anyJobRunning, startJobPolling, stopJobPolling, refetch]);

  const sidebarEnabled = useFeatureFlag("sidebar");
  const voicemailEnabled = useFeatureFlag("voicemailDrop");
  const multiplePassesEnabled = useFeatureFlag("multiplePasses");

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error && !isVanNotFoundError(error)) {
    return <ErrorCard errors={[error]} />;
  }

  const conditionalStep = (condition: boolean, step: StepDefinition) =>
    condition ? [step] : [];

  const detailsSteps: StepDefinition[] = [
    {
      name: "Basics",
      slug: "basics",
      component: Basics
    },
    {
      name: "Integration",
      slug: "integration",
      component: Integration
    },
    {
      name: "Write Questions",
      slug: "questions",
      component: Questions,
      validator: validateQuestionsSection
    },
    {
      name: "Add Contacts",
      slug: "contacts",
      component: Contacts,
      job: "contactImportJob",
      validator: validateContactsSection
    },
    ...conditionalStep(sidebarEnabled, {
      name: "Caller Sidebar",
      slug: "caller",
      component: CallerInterface
    }),
    {
      name: "Choose Caller ID",
      slug: "caller-id",
      component: Numbers
    },
    ...conditionalStep(voicemailEnabled, {
      name: "Leave Voicemail",
      slug: "voicemail",
      component: Voicemail
    }),
    ...conditionalStep(multiplePassesEnabled, {
      name: "Number of Passes",
      slug: "passes",
      component: MultiplePasses
    }),
    data?.campaign.status === CampaignStatus.DRAFT
      ? {
          name: "Start Campaign",
          slug: "start",
          component: StartCampaign,
          job: "startJob",
          runValidation: true
        }
      : {
          name: "Run Campaign",
          // same slug as "Start Campaign" so we don't have to redirect you
          // when the job completes
          slug: "start",
          component: RunCampaign
        },

    ...conditionalStep(
      data?.campaign.status !== CampaignStatus.DRAFT &&
        data?.campaign.startJob !== null,
      {
        name: "View Results",
        slug: "results",
        component: Results
      }
    ),

    // Hide Manage caller for draft
    ...conditionalStep(
      data?.campaign.status !== CampaignStatus.DRAFT &&
        data?.campaign.startJob !== null,
      {
        name: "Manage Callers",
        slug: "callers",
        component: Callers
      }
    )
  ];

  const activeStep =
    detailsSteps.findIndex(({ slug }) => slug === section) || 0;

  function renderStepName(name: string, job?: JobKey): string {
    // Return the step name, and the job progress if there's a related job
    if (!job) {
      return name;
    }

    const jobObj = data!.campaign[job];
    if (!jobObj) {
      return name;
    }

    if (
      jobObj.status !== JobStatus.QUEUED &&
      jobObj.status !== JobStatus.RUNNING
    ) {
      return name;
    }

    return `${name} (${(jobObj.progress * 100).toFixed(1)}%)`;
  }

  const goToStep = (step: number) => {
    history.push(subaccountLink(`/campaigns/${id}/${detailsSteps[step].slug}`));
  };

  const goToNextSection = () => {
    if (activeStep < detailsSteps.length - 1) {
      goToStep(activeStep + 1);
    }
  };

  const CurrentPage = detailsSteps[activeStep].component;

  const validationErrors: JSX.Element[] = [];
  const sectionsWithValidationErrors = new Set();
  if (detailsSteps[activeStep].runValidation) {
    detailsSteps.forEach(step => {
      if (step.validator) {
        const validationError = step.validator({
          tenant,
          campaign: data!.campaign,
          subaccountLink
        });

        if (validationError) {
          validationErrors.push(validationError);
          sectionsWithValidationErrors.add(step.slug);
        }
      }
    });
  }

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error && !isVanNotFoundError(error)) {
    return <ErrorCard errors={[error]} />;
  }

  return (
    <CampaignEditorsProvider
      campaign={data!.campaign}
      onRefetch={refetch}
      activeSection={section}
    >
      <div className={classes.title}>
        <PageTitle title={data!.campaign.name} />
      </div>
      <div className={classes.wrapper}>
        <div className={classes.stepperWrapper}>
          <Stepper nonLinear activeStep={activeStep} orientation="vertical">
            {detailsSteps.map(({ name, job, slug }, index) => (
              <Step key={name}>
                <StepButton onClick={() => goToStep(index)} completed={false}>
                  <StepLabel error={sectionsWithValidationErrors.has(slug)}>
                    {renderStepName(name, job)}
                  </StepLabel>
                </StepButton>
                <StepContent />
              </Step>
            ))}
          </Stepper>
        </div>
        <div className={classes.pageWrapper}>
          <Container>
            <CurrentPage
              campaign={data!.campaign}
              goToNextSection={goToNextSection}
              validationErrors={validationErrors}
              refreshCampaign={refetch}
            />
          </Container>
        </div>
      </div>
    </CampaignEditorsProvider>
  );
}
